/**
 * TimelineHistorico — Timeline & Historico do lead (.crm-tl canonico).
 *
 * Lista a `timeline` ja mesclada pelo backend (eventos + atendimentos +
 * compromissos, desc). Icone por origem/canal. O registro de contato deixou
 * de ser inline aqui: agora vive no RegistrarAtividadeDialog (acionado por
 * "Registrar contato" em Acoes rapidas).
 *
 * Cada registro aceita observacoes proprias (`item.comentarios`), presas ao
 * par (origem, id) — quem le a timeline ve o contexto junto do registro,
 * em vez de um "Comentario" solto la embaixo. `onComentar(item, texto)`
 * grava e a lista e' trocada pelo pai; `podeComentar` esconde o formulario
 * de quem so pode ver.
 *
 * Evento ATUALIZACAO traz `item.dados.mudancas` (antes/depois por campo,
 * CRM-29): a lista legivel substitui a descricao, que e o mesmo diff em
 * texto corrido cortado em 300 caracteres.
 */
import React, { useState } from "react";
import {
  FiCalendar,
  FiClipboard,
  FiFileText,
  FiMail,
  FiMessageCircle,
  FiMessageSquare,
  FiPhone,
  FiUsers,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import Block from "../../../components/Block";
import { CANAL_ATIVIDADE_LABEL } from "../../../services/crm";
import { EmptyMsg } from "../../Orcamentos/styles";

import { linhasDeMudancas } from "./mudancasDoLead";
import { ID_BLOCO_RESPOSTAS_FORMULARIO } from "./RespostasFormularioBlock";

// Icone por origem da timeline (evento/atendimento/compromisso) + canal.
function iconePara(item) {
  if (item.origem === "compromisso") return <FiCalendar />;
  if (item.origem === "atendimento") {
    const t = (item.titulo || "").toUpperCase();
    if (t.includes("EMAIL") || t.includes("E-MAIL")) return <FiMail />;
    if (t.includes("WHATSAPP")) return <FiMessageCircle />;
    if (t.includes("REUNI")) return <FiUsers />;
    return <FiPhone />;
  }
  // eventos genericos (mudanca de etapa, comentario, criacao, ...)
  const t = (item.tipo || "").toUpperCase();
  if (t.includes("COMENT")) return <FiMessageSquare />;
  if (t === TIPO_FORMULARIO_RESPONDIDO) return <FiClipboard />;
  return <FiFileText />;
}

// Evento FORMULARIO_RESPONDIDO (FORM-18): atalho para o bloco de respostas.
const TIPO_FORMULARIO_RESPONDIDO = "FORMULARIO_RESPONDIDO";

function irParaRespostas() {
  const bloco = document.getElementById(ID_BLOCO_RESPOSTAS_FORMULARIO);
  bloco?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function fmtQuando(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return "";
  }
}

const MESES_ABREV = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez",
];

// Formato pedido (refinamento ID 30): "Hoje às 15:49" se for hoje;
// "Ontem (21/04) às 15:49" se for ontem; senao "21 abr às 15:49".
function fmtTimelineQuando(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hora = `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
  const chave = (x) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  const hoje = new Date();
  const ontem = new Date(hoje);
  ontem.setDate(hoje.getDate() - 1);
  if (chave(d) === chave(hoje)) return `Hoje às ${hora}`;
  if (chave(d) === chave(ontem)) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `Ontem (${dd}/${mm}) às ${hora}`;
  }
  return `${d.getDate()} ${MESES_ABREV[d.getMonth()]} às ${hora}`;
}

/** "Rótulo: antes → depois" por campo alterado (evento ATUALIZACAO). */
function MudancasDoRegistro({ linhas }) {
  return (
    <ul className="tl-mudancas" aria-label="Campos alterados">
      {linhas.map((m) => (
        <li key={m.campo}>
          <span className="tl-mudancas__campo">{m.rotulo}:</span>{" "}
          <s>{m.antes}</s> → <strong>{m.depois}</strong>
        </li>
      ))}
    </ul>
  );
}

/** Observacoes de UM registro + formulario inline para adicionar outra. */
function ComentariosDoRegistro({ item, podeComentar, onComentar }) {
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState("");
  const comentarios = Array.isArray(item.comentarios) ? item.comentarios : [];

  const fechar = () => {
    setAberto(false);
    setTexto("");
  };

  const salvar = async () => {
    const limpo = texto.trim();
    if (!limpo) return;
    await onComentar(item, limpo);
    fechar();
  };

  if (comentarios.length === 0 && !podeComentar) return null;

  return (
    <div className="tl-coments">
      {comentarios.map((c) => (
        <div className="tl-coment" key={c.id}>
          <div className="tl-coment__texto">{c.texto}</div>
          <div className="tl-coment__meta" title={fmtQuando(c.created_at)}>
            {c.autor_nome} · {fmtTimelineQuando(c.created_at)}
          </div>
        </div>
      ))}
      {podeComentar && !aberto && (
        <button
          type="button"
          className="tl-coment-add"
          onClick={() => setAberto(true)}
          aria-label={`Comentar o registro ${item.titulo || ""}`.trim()}
        >
          <FiMessageSquare aria-hidden="true" /> Comentar
        </button>
      )}
      {podeComentar && aberto && (
        <div className="tl-coment-form">
          <textarea
            className="textarea"
            rows={2}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Observação sobre este registro"
            aria-label="Observação sobre este registro"
            autoFocus
          />
          <div className="tl-coment-form__acoes">
            <button type="button" className="btn btn--secondary" onClick={fechar}>
              Cancelar
            </button>
            <AsyncButton
              variant="primary"
              onClick={salvar}
              disabled={!texto.trim()}
              successMessage="Observação registrada."
              silentError
            >
              Salvar
            </AsyncButton>
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineHistorico({
  timeline,
  podeComentar = false,
  onComentar,
  collapsible,
  defaultCollapsed,
}) {
  const itens = Array.isArray(timeline) ? timeline : [];
  const aceitaComentario = Boolean(podeComentar && onComentar);

  return (
    <Block
      title="Timeline & histórico"
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
    >
      {itens.length === 0 ? (
        <EmptyMsg style={{ padding: 8 }}>
          Sem interações ainda. Registre o primeiro contato em
          &quot;Ações rápidas&quot;.
        </EmptyMsg>
      ) : (
        <ul className="crm-tl">
          {itens.map((item, idx) => {
            const canal =
              item.origem === "atendimento" && CANAL_ATIVIDADE_LABEL[item.canal];
            const mudancas = linhasDeMudancas(item.dados);
            return (
              <li key={item.id ? `${item.origem}-${item.id}` : `${item.origem}-${item.timestamp}-${idx}`}>
                <span className="marker" aria-hidden="true">
                  {iconePara(item)}
                </span>
                <div className="item-body">
                  <div className="ttl">{item.titulo || "Evento"}</div>
                  {mudancas.length > 0 ? (
                    <MudancasDoRegistro linhas={mudancas} />
                  ) : (
                    item.descricao && <div className="sub">{item.descricao}</div>
                  )}
                  <div className="sub">
                    {item.ator_nome && <>por {item.ator_nome}</>}
                    {canal && <> · {canal}</>}
                    {item.tipo === TIPO_FORMULARIO_RESPONDIDO && (
                      <>
                        {" · "}
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={irParaRespostas}
                        >
                          Ver respostas
                        </button>
                      </>
                    )}
                  </div>
                  {item.id && (
                    <ComentariosDoRegistro
                      item={item}
                      podeComentar={aceitaComentario}
                      onComentar={onComentar}
                    />
                  )}
                </div>
                <div className="item-side" title={fmtQuando(item.timestamp)}>
                  {fmtTimelineQuando(item.timestamp)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Block>
  );
}

export default TimelineHistorico;
